import { useEffect, useMemo, useState } from "react";
import { FileText, ExternalLink } from "lucide-react";
import { PortalPage, Card, Btn, Spinner, ErrorNote } from "@/components/PortalPage";
import { Accordion, Paragraphs, SearchBox, useSingleOpen } from "@/components/ContentParts";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/lso";

const openMaster = (m) => {
  if (m.pdf_url) return window.open(m.pdf_url, "_blank");
  const b64 = (m.pdf_base64 || "").replace(/^data:[^,]+,/, "");
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  window.open(URL.createObjectURL(new Blob([bytes], { type: "application/pdf" })), "_blank");
};

export default function Operations() {
  const [sections, setSections] = useState(null);
  const [master, setMaster] = useState(null);
  const [q, setQ] = useState("");
  const [openId, toggle] = useSingleOpen();
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([api("/manual/sections"), api("/manual/master").catch(() => null)])
      .then(([s, m]) => { setSections(s); setMaster(m); })
      .catch((e) => setErr(e.message));
  }, []);

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const hit = (sections || []).filter((s) => !needle || s.title.toLowerCase().includes(needle) || (s.content || "").toLowerCase().includes(needle));
    const map = new Map();
    hit.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).forEach((s) => {
      const c = s.category || "General";
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(s);
    });
    return [...map.entries()];
  }, [sections, q]);

  return (
    <PortalPage eyebrow="ALR OPERATIONS MANUAL" title="Operations Manual" testId="operations-page">
      {master?.has_pdf && (
        <Card className="flex items-center gap-4" data-testid="ops-master-card">
          <FileText size={28} className="shrink-0 text-[var(--accent)]" />
          <div className="flex-1"><p className="font-bold">Complete manual (PDF)</p><p className="text-xs text-neutral-400">{master.file_name}{master.uploaded_at ? ` · updated ${fmtDate(master.uploaded_at)}` : ""}</p></div>
          <Btn className="!w-auto" onClick={() => openMaster(master)} data-testid="ops-open-master"><ExternalLink size={16} /> Open</Btn>
        </Card>
      )}
      <SearchBox value={q} onChange={setQ} placeholder="Search the manual…" testId="ops-search" />
      <ErrorNote>{err}</ErrorNote>
      {!sections && !err && <Spinner />}
      {sections && grouped.length === 0 && <p className="text-sm text-neutral-400" data-testid="ops-empty">No sections match "{q}".</p>}
      {grouped.map(([cat, list]) => (
        <section key={cat} className="space-y-2" data-testid={`ops-category-${cat.toLowerCase().replace(/\W+/g, "-")}`}>
          <h2 className="pt-3 text-[11px] font-bold tracking-[0.3em] text-neutral-400">{cat.toUpperCase()} <span className="text-neutral-600">· {list.length}</span></h2>
          {list.map((s) => (
            <Accordion key={s.id} id={s.id} title={s.title} open={openId === s.id || (!!q.trim() && list.length <= 3)} onToggle={() => toggle(s.id)}>
              <Paragraphs text={s.content} />
            </Accordion>
          ))}
        </section>
      ))}
    </PortalPage>
  );
}
